import { cn } from '@workspace/ui/lib/utils';
import type React from 'react';

import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import type { IconTypes } from '~/components/Icon/IconTypes';

interface SocialItem {
  key: string;
  href: string;
  dataLink: string;
  rel: string;
  icon: IconTypes;
}

interface FooterSocialLinksProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  iconSize?: 20 | 24;
  outlined?: boolean;
  children?: React.ReactNode;
  variant?: 'default' | 'light-outline';
}

const itemClassName =
  'flex size-10 shrink-0 items-center justify-center transition-colors duration-300 rounded-full text-[var(--navigation-text-primary)]! no-underline hover:bg-[var(--navigation-surface-2)]';

export const FooterSocialLinks = ({
  iconSize = 24,
  outlined = true,
  variant = 'default',
  className,
  children,
  ...props
}: FooterSocialLinksProps) => {
  const { links } = company.config;

  const suffix = outlined ? '-outlined' : '';

  const socials: SocialItem[] = [
    links.instagram && {
      key: 'instagram',
      href: links.instagram,
      dataLink: 'footer-social-instagram',
      rel: 'noreferrer',
      icon: `insta${suffix}` as IconTypes,
    },
    links.facebook && {
      key: 'facebook',
      href: links.facebook,
      dataLink: 'footer-social-facebook',
      rel: 'noreferrer',
      icon: `fb${suffix}` as IconTypes,
    },
    links.tiktok && {
      key: 'tiktok',
      href: links.tiktok,
      dataLink: 'footer-social-tiktok',
      rel: 'noreferrer nofollow',
      icon: `tiktok${suffix}` as IconTypes,
    },
    links.youtube && {
      key: 'youtube',
      href: links.youtube,
      dataLink: 'footer-social-youtube',
      rel: 'noreferrer',
      icon: `youtube${suffix}` as IconTypes,
    },
  ].filter(Boolean) as SocialItem[];

  return (
    <div {...props} className={cn('flex items-center gap-2', className)}>
      {socials.map(({ key, href, dataLink, rel, icon }) => (
        <a
          key={key}
          data-link={dataLink}
          href={href}
          target="_blank"
          rel={rel}
          className={cn(
            itemClassName,
            variant === 'light-outline'
              ? 'outline! text-navigation-text-primary! outline-px outline-stroke-primary! hover:text-navigation-text-primary!'
              : 'bg-surface-inverse-transparent-3 text-text-inverse-primary! outline-stroke-inverse-primary! hover:text-text-inverse-primary!',
          )}
        >
          <Icon type={icon} size={iconSize} />
        </a>
      ))}
      {children}
    </div>
  );
};
